/**
 * Auth + portal-context store.
 *
 * Class with private $state fields and public getters, exported as a singleton —
 * the pattern plugins/augurly and plugins/augurly_admin both use.
 *
 * Two deliberate differences from plugins/augurly's version:
 *
 *  1. `isAuthenticated` consults `isTokenValid()`. Augurly's checks only that a
 *     token and user object exist, so an expired token still reads as
 *     authenticated until something 401s.
 *  2. Role and scope come from an explicit `GET /strao/me/context` call, held
 *     here. Augurly infers its own privilege level by calling an admin endpoint
 *     and string-matching the resulting 403 — that races, and it cannot express
 *     scope at all, only a boolean.
 */

import api from '$lib/api.js';
import {
  clearAuth as clearStoredAuth,
  getToken,
  getUser,
  isTokenValid,
  setToken,
  setUser,
} from '$lib/authUtils.js';

class AuthStore {
  #token = $state(null);
  #user = $state(null);
  #context = $state(null);
  #loading = $state(true);
  #error = $state(null);

  // Not $state: an in-flight dedup handle, never rendered.
  #contextPromise = null;

  constructor() {
    this.#restore();
  }

  #restore() {
    const token = getToken();
    if (isTokenValid(token)) {
      this.#token = token;
      this.#user = getUser();
    } else if (token) {
      // Expired on load — clear rather than carry a token that will 401.
      clearStoredAuth();
    }
    this.#loading = false;
  }

  get token() { return this.#token; }
  get user() { return this.#user; }
  get context() { return this.#context; }
  get loading() { return this.#loading; }
  get error() { return this.#error; }

  get isAuthenticated() {
    return !!this.#user && isTokenValid(this.#token);
  }

  // -- portal role/scope, from GET /strao/me/context --

  get role() { return this.#context?.role ?? null; }
  get isSuperUser() { return this.role === 'super_user'; }
  get isPmStaff() { return this.role === 'pm_staff'; }
  get isPropertyOwner() { return this.role === 'property_owner'; }
  get isStaff() { return this.isSuperUser || this.isPmStaff; }

  get canViewRevenue() { return !!this.#context?.can_view_revenue; }
  get canManageUsers() { return !!this.#context?.can_manage_users; }
  get canManageTenants() { return !!this.#context?.can_manage_tenants; }
  get canSwitchTenants() { return !!this.#context?.can_switch_tenants; }
  get canViewUnlinked() { return !!this.#context?.can_view_unlinked; }

  get tenantId() { return this.#context?.tenant_id ?? null; }
  get availableTenants() { return this.#context?.available_tenants ?? []; }

  /** null means "all properties in tenant"; [] means none. */
  get properties() { return this.#context?.properties ?? null; }

  canAccessProperty(propertyId) {
    const props = this.properties;
    if (props === null) return true;
    return props.some((p) => p.id === propertyId);
  }

  get displayName() {
    return this.#context?.full_name || this.#user?.first_name || this.#user?.email || 'Account';
  }

  // -- mutations --

  setAuth(user, token) {
    this.#user = user;
    this.#token = token;
    setUser(user);
    setToken(token);
    this.#error = null;
  }

  setContext(context) { this.#context = context; }
  setLoading(value) { this.#loading = value; }
  setError(message) { this.#error = message; }
  clearError() { this.#error = null; }

  clearAuth() {
    this.#user = null;
    this.#token = null;
    this.#context = null;
    clearStoredAuth();
  }

  /** Load (or reload, on tenant switch) the portal context. */
  async loadContext(tenantId) {
    if (!this.isAuthenticated) return null;
    const context = await api.getContext(tenantId);
    this.#context = context;
    return context;
  }

  /**
   * Resolve the portal context, loading it at most once.
   *
   * Needed because two independent callers want it on a cold load: the route
   * guard (which must know the role BEFORE deciding whether to admit a
   * navigation) and the shell (which needs it to render the sidebar). Without
   * the in-flight promise cache, a hard refresh onto a role-gated URL fires two
   * concurrent GET /strao/me/context calls, and the guard can also observe a
   * null role mid-flight and wrongly bounce a legitimate super-user to the
   * dashboard.
   */
  async ensureContext() {
    if (!this.isAuthenticated) return null;
    if (this.#context) return this.#context;
    if (!this.#contextPromise) {
      this.#contextPromise = this.loadContext().finally(() => {
        this.#contextPromise = null;
      });
    }
    return this.#contextPromise;
  }
}

export const authStore = new AuthStore();
export default authStore;
